import React, { useState, useEffect } from "react";
import { Table, Card, Tabs, Button, InputNumber, message, Space, Alert, Image, Typography, Row, Col, Divider, Input } from "antd";
import axios from "axios";

const { Title, Text } = Typography;

const MarkovAnalysis = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("input");
  const [numStates, setNumStates] = useState(3);
  const [inputData, setInputData] = useState({
    num_states: 3,
    initial_probabilities: [0.33, 0.33, 0.34],
    transition_matrix: [
      [0.8, 0.1, 0.1],
      [0.2, 0.7, 0.1],
      [0.3, 0.3, 0.4]
    ],
    transitions: 5
  });
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const getAuthToken = () => localStorage.getItem("access_token");

  // Auth: Logout on 401
  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    window.location.reload();
  };

  useEffect(() => {
    if (fileId) {
      loadData(fileId);
    } else {
      const ns = initialConfig?.num_states || 3;
      const t = initialConfig?.transitions || 5;
      setNumStates(ns);

      // Initialize with equal probabilities and identity matrix if not provided
      const initialProb = new Array(ns).fill(1 / ns);
      const matrix = Array.from({ length: ns }, (_, i) =>
        Array.from({ length: ns }, (_, j) => (i === j ? 1 : 0))
      );

      setInputData({
        num_states: ns,
        initial_probabilities: initialProb,
        transition_matrix: matrix,
        transitions: t
      });
      setResults(null);
      setIsSaved(false);
      setActiveTab("input");
    }
  }, [fileId, initialConfig]);

  const loadData = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");
      setIsLoading(true);

      const res = await axios.get(
        `/api/retrieve_Markov_Analysis/${id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setInputData(res.data.input_data);
      setNumStates(res.data.input_data.num_states);
      setResults(res.data.output_data);
      setIsSaved(true);
      setActiveTab("output");
    } catch (err) {
      console.error("Load error:", err);
      if (err.response?.status === 401) handleLogout();
      message.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const saveData = async () => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");

      setIsLoading(true);

      const payload = {
        name: fileName || "Markov Analysis",
        input_data: inputData,
      };

      let res;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (fileId) {
        res = await axios.put(
          `/api/update_Markov_Analysis/${fileId}/`,
          payload,
          config
        );
      } else {
        res = await axios.post(
          `/api/save_Markov_Analysis/`,
          payload,
          config
        );
      }

      setResults(res.data.output_data);

      // Update selectedFile to prevent duplicate entries
      if (!fileId && setSelectedFile) {
        setSelectedFile({
          id: res.data.id,
          name: res.data.name,
          type: "markov_analysis",
          inputData: res.data.input_data
        });
      }

      setIsSaved(true);
      setActiveTab("output");
      message.success(fileId ? "Updated successfully" : "Saved successfully");
    } catch (err) {
      console.error("Save error:", err);
      if (err.response?.status === 401) handleLogout();
      message.error(`Failed to save: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProbChange = (index, val) => {
    const newProbs = [...inputData.initial_probabilities];
    newProbs[index] = val;
    setInputData({ ...inputData, initial_probabilities: newProbs });
  };

  const handleMatrixChange = (row, col, val) => {
    const newMatrix = inputData.transition_matrix.map(r => [...r]);
    newMatrix[row][col] = val;
    setInputData({ ...inputData, transition_matrix: newMatrix });
  };

  const handleTransitionsChange = (val) => {
    setInputData({ ...inputData, transitions: val });
  };

  const renderInputForm = () => (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={5}>Transitions (Periods):</Title>
          <InputNumber
            min={1}
            value={inputData.transitions}
            onChange={handleTransitionsChange}
            style={{ width: 120 }}
          />
        </div>

        <div>
          <Title level={5}>Initial Probabilities:</Title>
          <Row gutter={[8, 8]}>
            {inputData.initial_probabilities.map((prob, idx) => (
              <Col key={idx}>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary">State {idx + 1}</Text><br />
                  <InputNumber
                    min={0}
                    max={1}
                    step={0.01}
                    value={prob}
                    onChange={(v) => handleProbChange(idx, v)}
                    style={{ width: 100 }}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </div>

        <div>
          <Title level={5}>Transition Matrix:</Title>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: '8px' }}>
              <thead>
                <tr>
                  <th></th>
                  {Array.from({ length: numStates }).map((_, i) => (
                    <th key={i}><Text type="secondary">To {i + 1}</Text></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inputData.transition_matrix.map((row, rIdx) => (
                  <tr key={rIdx}>
                    <td><Text type="secondary">From {rIdx + 1}</Text></td>
                    {row.map((val, cIdx) => (
                      <td key={cIdx}>
                        <InputNumber
                          min={0}
                          max={1}
                          step={0.01}
                          value={val}
                          onChange={(v) => handleMatrixChange(rIdx, cIdx, v)}
                          style={{ width: 100 }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Button
          type="primary"
          onClick={saveData}
          loading={isLoading}
          style={{ marginTop: 24 }}
          block
        >
          {isSaved ? "Update Analysis" : "Calculate & Save"}
        </Button>
      </Space>
    </Card>
  );

  const renderOutputTable = () => {
    if (!results) {
      return (
        <Alert
          message="Results not available"
          description="Please enter input data and click Calculate."
          type="info"
          showIcon
        />
      );
    }

    const {
      period_results,
      steady_state,
      state_types,
      class_numbers,
      ending_probabilities,
      B_matrix,
      F_matrix,
      FA_matrix,
      states
    } = results;

    // Table for Period Results
    const periodColumns = [
      { title: "Period", dataIndex: "period", key: "period", fixed: 'left', width: 80 },
      ...states.map((s, idx) => ({
        title: s,
        dataIndex: ["probabilities", idx],
        key: `state_${idx}`,
        render: (val) => val.toFixed(4)
      }))
    ];

    // Table for State Analysis
    const stateAnalysisData = states.map((s, idx) => ({
      key: idx,
      state: s,
      ending: ending_probabilities[idx],
      steady: steady_state[idx],
      type: state_types[idx],
      class: class_numbers[idx]
    }));

    const stateAnalysisColumns = [
      { title: "State", dataIndex: "state", key: "state" },
      { title: "Ending Probability", dataIndex: "ending", key: "ending", render: (v) => v.toFixed(4) },
      { title: "Steady State", dataIndex: "steady", key: "steady", render: (v) => v.toFixed(4) },
      { title: "Type", dataIndex: "type", key: "type" },
      { title: "Class", dataIndex: "class", key: "class" },
    ];

    const renderMatrix = (matrix, title, rowLabels, colLabels) => {
      if (!matrix || matrix.length === 0) return null;
      const cols = [
        { title: "", dataIndex: "label", key: "label", bold: true },
        ...colLabels.map((l, i) => ({
          title: l,
          dataIndex: i,
          key: i,
          render: (v) => v.toFixed(4)
        }))
      ];
      const data = matrix.map((row, i) => ({
        key: i,
        label: rowLabels[i],
        ...row
      }));

      return (
        <Card title={title} size="small" style={{ marginTop: 16 }}>
          <Table
            dataSource={data}
            columns={cols}
            pagination={false}
            size="small"
            bordered
          />
        </Card>
      );
    };

    const transientStates = states.filter((_, i) => state_types[i] === "Transient");
    const recurrentStates = states.filter((_, i) => state_types[i] === "Recurrent");

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card title="State Analysis Summary">
          <Table
            dataSource={stateAnalysisData}
            columns={stateAnalysisColumns}
            pagination={false}
            size="small"
            bordered
          />
        </Card>

        <Card title="Probabilities by Period">
          <Table
            dataSource={period_results}
            columns={periodColumns}
            pagination={{ pageSize: 10 }}
            size="small"
            rowKey="period"
            scroll={{ x: 'max-content' }}
          />
        </Card>

        {transientStates.length > 0 && (
          <div style={{ background: '#f0f2f5', padding: '16px', borderRadius: '8px' }}>
            <Title level={4}>Absorbtion Analysis (Sub-matrices)</Title>
            {renderMatrix(B_matrix, "B Matrix (Transient to Transient)", transientStates, transientStates)}
            {renderMatrix(F_matrix, "F Fundamental Matrix (I - B)^-1", transientStates, transientStates)}
            {renderMatrix(FA_matrix, "FA Matrix (Probability of Absorption)", transientStates, recurrentStates)}
          </div>
        )}

        {results.chart_url && (
          <Card title="State Probability Trends">
            <Image
              src={results.chart_url}
              alt="Markov Trends"
              style={{ width: '100%', maxWidth: '800px' }}
              fallback="https://via.placeholder.com/800x400?text=Markov+Analysis+Graph"
            />
          </Card>
        )}
      </Space>
    );
  };

  return (
    <Card
      title={fileName || "Markov Analysis"}
      style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "input",
            label: "Inputs",
            children: renderInputForm()
          },
          {
            key: "output",
            label: "Results",
            disabled: !results,
            children: renderOutputTable()
          }
        ]}
      />
    </Card>
  );
};

export default MarkovAnalysis;
